import { Injectable } from '@angular/core';
import { EventBusService } from '@app/core/services/event-bus/event-bus.service';
import { ToastService } from '@app/core/services/toast/toast.service';
import {
  AccountingExportGQL,
  AccountingExportQuery,
  GenerateAccountingExportGQL,
  GenerateAccountingExportMutation,
} from '@app/generated/graphql';
import { Apollo } from 'apollo-angular';
import { ApolloQueryResult } from '@apollo/client/core';
import * as Sentry from '@sentry/browser';
import { Observable, of } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import { ActiveBrowserTabService } from '@app/core/services/active-browser-tab/active-browser-tab.service';

@Injectable({
  providedIn: 'root',
})
export class AccountingExportService {
  constructor(
    private eventBus: EventBusService,
    private apollo: Apollo,
    private generateAccountingExportGQL: GenerateAccountingExportGQL,
    private accountingExportGQL: AccountingExportGQL,
    private toastService: ToastService,
    private activeBrowserTabService: ActiveBrowserTabService,
  ) {}

  public setupAccountingExportSubscription(): void {
    const eventBus$ = this.getAccountingExportIdStream();

    eventBus$
      .pipe(
        switchMap((exportId: string) => {
          return this.fetchAccountingExport(exportId);
        }),
        tap((res: ApolloQueryResult<AccountingExportQuery>) => {
          this.handleAccountingExportResult(res);
        }),
        catchError(this.handleError),
      )
      .subscribe();
  }

  public generateAccountingExport(invoiceIds: string[]): void {
    this.apollo
      .mutate<GenerateAccountingExportMutation>({
        mutation: this.generateAccountingExportGQL.document,
        variables: {
          invoiceIds: invoiceIds,
        },
      })
      .subscribe(
        () => {
          this.toastService.showToast('Generating document');
        },
        (err) => {
          this.toastService.showErrorToast(
            'Failed to generate accounting export',
          );
          Sentry.captureException(err);
        },
      );
  }

  private getAccountingExportIdStream(): Observable<string> {
    return this.eventBus
      .getChannel('RefetchAccountingExport', false)
      .pipe(filter((id: string) => typeof id === 'string' && id.length > 0));
  }

  private fetchAccountingExport(
    exportId: string,
  ): Observable<ApolloQueryResult<AccountingExportQuery>> {
    return this.apollo.query<AccountingExportQuery>({
      query: this.accountingExportGQL.document,
      variables: { id: exportId },
    });
  }

  private handleAccountingExportResult(
    res: ApolloQueryResult<AccountingExportQuery>,
  ): void {
    const accountingExport = res.data?.accountingExport;
    const isActiveTab = this.activeBrowserTabService.isTabActive;

    if (
      accountingExport?.exportStatus === 'exported' &&
      accountingExport.exportFile?.v2Url &&
      isActiveTab
    ) {
      location.href = accountingExport.exportFile.v2Url;
    } else if (accountingExport?.exportStatus === 'failed') {
      this.toastService.showErrorToast('Failed to generate accounting export');
    }
  }

  private handleError(error: any): Observable<null> {
    console.error('Error fetching accounting export:', error);
    this.toastService.showErrorToast('Failed to fetch accounting export');
    Sentry.captureException(error);
    return of(null);
  }
}
